import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { NoticeQuery } from "../../../generated/app-service-gql/graphql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const noticeQuery = graphql(`
  query notice($id: String!) {
    notice(noticeId: $id) {
      guid
      name
      description
      entityType
      stage
      stagev2
      isHidden
      score
      value
      currency
      url
      expiryDate
      closeDate
      publishDate
      awardDate
      buyerContactName
      buyerContactEmail
      buyerContactPhone
      procurementStage {
        id
        stage
        procurementProcessId
      }
      lots {
        lotNumber
        lotTitle
        value
        currency
        endDate
        suppliers {
          id
          guid
          name
        }
      }
      lotsv2 {
        id
        title
        description
        value {
          amount
          currency
        }
        cpvCodes {
          code
          name
        }
        contractPeriod {
          startDate
          maxExtentDate
          endDate
        }
      }
      contracts {
        awardId
        title
        description
        value {
          amount
          currency
        }
        awardDate
        contractPeriod {
          startDate
          maxExtentDate
          endDate
        }
        suppliers {
          name
          guid
        }
        cpvCodes {
          code
          name
        }
        lots {
          id
          title
        }
      }
      cpvCodes {
        code
        name
      }
      suppliers {
        id
        name
        guid
      }
      buyers {
        id
        guid
        name
        buyerUrl
        country
        town
        postalCode
        address
      }
      assignee {
        guid
        firstName
        lastName
      }
      seenByUsers {
        guid
        firstName
      }
      relatedFrameworkProcess {
        frameworkId
        frameworkName
        processType
      }
      signals {
        id
        name
        category
      }
      timeline {
        preTender {
          title
          stage
          publishedDate
          deadlineDates
        }
        tender {
          title
          stage
          estimated
          publishedDate
          clarificationDate
          closeDate
        }
        award {
          title
          stage
          estimated
          publishedDate
          awardDate
          startDate
          expiryDate
          extensionDate
        }
      }
      stageNotices {
        stage
        guid
      }
      redacted
      duplicates {
        guid
        name
        url
      }
      sourceNotices {
        id
        noticeType
        publishDate
        url
      }
      aiSummary {
        mentionedParties
        topicRelevance
        thingBeingProcured
        contractValue
        bidRequirements
      }
      leads {
        id
        name
      }
      hasBuyerLeads
      bid {
        id
        name
      }
      comments {
        id
        content
        createdAt
        updatedAt
        author {
          guid
          firstName
          lastName
        }
      }
    }
  }
`);

export type NoticeDetails = NoticeQuery["notice"];

export function useNotice(
  noticeId: string,
  options?: UseQueryOptions<NoticeQuery, unknown, NoticeQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(["notice", noticeId], noticeQuery, [{ id: noticeId }], {
    ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
    ...options,
  });

  return { data: data?.notice, ...rest };
}
